// editorPlugins.js
// Configure all editor plugins

import {createCollaborationPluginBundle} from "./modules/collaborationPluginBundle";
import {createCorePluginBundle} from "./modules/corePluginBundle";
import {createInputKeymaps} from "./modules/inputKeymaps";
import {createMentionPluginBundle} from "./modules/mentionPluginBundle";
import {createInlineInteractionPopoverPlugin} from "./inlineInteractionPopoverPlugin";
import {createOptionalPluginBundle} from "./modules/optionalPluginBundle";
import {createInlineInteractionPlugin} from "./inlineInteractionPlugin";
import {createSuggestionModePlugin} from "./suggestionModePlugin";
import {createCitationRefPopoverPlugin} from "./citationRefPopoverPlugin";

/**
 * Create all editor plugins
 * @param {Object} options - Configuration options
 * @param {Object} options.yXmlFragment - Yjs XML fragment for collaboration
 * @param {Object} options.awareness - Yjs awareness for cursors
 * @param {Object} options.user - Current user object
 * @param {Object} options.provider - Yjs WebSocket provider for image uploads
 * @param {string} options.documentId - Document ID for Firestore updates
 * @param {boolean} options.collaboration - Enable collaboration features (default: true)
 * @param {Object} options.snippetDrop - Snippet drop plugin options (reportId, onReferenceAdded, addToSourcePackage)
 * @param {Function} options.onInlineInteractionChange - Callback when inline interaction target changes (payload or null)
 * @param {Function} options.onSelectionChange - Callback when selection state changes (hasSelection: boolean)
 * @param {Function} options.onEntityClick - Legacy callback when Wikidata entity mark is clicked
 * @param {Function} options.onUserMentionClick - Legacy callback when user mention is clicked
 * @param {Function} options.onHashtagClick - Legacy callback when hashtag is clicked
 * @param {Function} options.onInlineInteractionClear - Legacy callback when caret leaves interaction targets
 * @param {Object} options.hashtagOptions - Hashtag configuration options
 * @param {string} options.hashtagOptions.segmentId - Segment ID for AI-powered tag suggestions
 * @param {boolean} options.hashtagOptions.useAISuggestions - Enable AI suggestions (default: false)
 * @param {Object} options.yDocMeta - Y.Map reference for document metadata
 * @param {Object} options.ydoc - Y.Doc reference for transactions
 * @param {Object} options.yReferenceMarks - Y.Map("referenceMarks") — included in UndoManager scope
 *                                           so that adding/removing references is undoable with Ctrl+Z
 * @param {Object} options.yComments - Y.Map("comments") — included in UndoManager scope so that
 *                                     deleting a comment marker (and its thread) is fully undoable
 * @returns {Array<Plugin>} Array of ProseMirror plugins
 */
export function createEditorPlugins(options = {}) {
    const {
        yXmlFragment,
        awareness,
        user,
        provider,
        documentId,
        classificationMenu,
        collaboration = true,
        snippetDrop,
        onInlineInteractionChange,
        onEntityClick,
        onUserMentionClick,
        onHashtagClick,
        onInlineInteractionClear,
        onSelectionChange,
        hashtagOptions = {},
        inlineInteractionPopovers = false,
        yDocMeta,
        ydoc,
        yReferenceMarks,
        yComments,
        editorMode = 'full',
        schema,
    } = options;

    const plugins = [];
    const collaborationEnabled = Boolean(collaboration && yXmlFragment);
    const basic = editorMode === 'basic';

    // suggestChanges() registers the plugin-state key and enables the
    // withSuggestChanges dispatchTransaction decorator (wired in createEditorView).
    // It takes no arguments — user identity is not needed by the new library.
    plugins.push(createSuggestionModePlugin());

    plugins.push(...createCollaborationPluginBundle({
        collaborationEnabled,
        yXmlFragment,
        awareness,
        yReferenceMarks,
        yComments,
    }));

    plugins.push(...createCorePluginBundle({
        user,
        snippetDrop: basic ? null : snippetDrop,
        yReferenceMarks,
        ydoc,
        classificationMenu,
        documentId,
        yDocMeta,
        editorMode,
        schema,
    }));

    if (!basic) {
        plugins.push(...createMentionPluginBundle({
            documentId,
            hashtagOptions,
        }));

        if (inlineInteractionPopovers) {
            plugins.push(createInlineInteractionPopoverPlugin());
        }

        // MINSKY-16823 (ticket 04): citation-ref hover/click popover — always registered
        // alongside the mention popovers (not gated behind inlineInteractionPopovers) since
        // citationRef marks can appear in any full-mode editor a Forecast is added to, not just
        // the surfaces that opt into the mention/hashtag/wikidata caret popovers.
        plugins.push(createCitationRefPopoverPlugin());

        if (onInlineInteractionChange || onEntityClick || onUserMentionClick || onHashtagClick || onInlineInteractionClear) {
            plugins.push(createInlineInteractionPlugin({
                onInlineInteractionChange,
                onEntityClick,
                onUserMentionClick,
                onHashtagClick,
                onInlineInteractionClear,
            }));
        }
    }

    plugins.push(...createOptionalPluginBundle({
        collaborationEnabled,
        yXmlFragment,
        onSelectionChange,
        snippetDrop: basic ? null : snippetDrop,
        provider,
        user,
    }));

    plugins.push(...createInputKeymaps({
        collaborationEnabled,
    }));

    return plugins;
}
